import { api } from '@/global/services/api'
import truncatedTextDisplay from '@/global/components/TruncatedTextDisplay.vue'
import { isObject } from 'lodash'
import { notify } from '@/global/services/notification/notificationBus'
import { showDetailsAction } from '@/global/components/table/helpers/tableActions'
import tableMixin from '@/global/mixins/tableMixin'
import { dateService, FORMATS } from '@/global/services/date'
import { STATUS_PILL_CELL_CONFIG } from '../../../common/config/status-cell-helper'

export default {
  name: 'mainTableConfig',

  mixins: [
    tableMixin
  ],

  data () {
    return {
      indexTableFiltersDropdowns: {}
    }
  },

  computed: {
    tableConfig () {
      return {
        tableConfig: {
          uiView: 'eRegulation_roads_index',
          apiConfig: {
            get: {
              module: 'road-maintenance',
              route: 'traffic-regulation/roads',
              method: 'get'
            }
          },
          rowDblClick: (event) => this.showRequestDetails(event),
          title_and_back_config: {
            title: this.$t('road-maintenance/aggregator.streets_overview')
          },
          search_and_buttons_config: {
            additional_buttons: [
              {
                label: this.$t('base.export'),
                src: '/img/icons/export-icon.svg',
                visible: true,
                click: async (event) => this.onExportRoads(event)
              }
            ]
          },
          tableConfig: {
            cellsConfig: {
              road: {
                custom_display_value: (event) => {
                  return event?.road?.name ? event.road.name : ''
                }
              },
              road_type: {
                custom_display_value: (event) => {
                  return event?.road_type?.description ? this.$t(event.road_type.description) : ''
                }
              },
              city_district: {
                custom_display_value: (event) => {
                  return event?.city_district?.name ? event.city_district.name : ''
                }
              },
              status: {
                type: 'status',
                custom_display_value: (data) => {
                  if (data && data.status && isObject(data.status)) {
                    return {
                      ...data.status,
                      ...{
                        description: data.status.description ? this.$t(data.status.description) : '-'
                      }
                    }
                  }
                },
                displayValue: 'description',
                colorObjectKey: 'color',
                textColorKey: 'text',
                backgroundColorKey: 'background',
                ...STATUS_PILL_CELL_CONFIG
              }
            },
            headers: [
              {
                label: this.$t('road-maintenance/aggregator.street'),
                key: 'road',
                component: truncatedTextDisplay,
                always_visible: true
              },
              {
                label: this.$t('road-maintenance/aggregator.house_number'),
                key: 'address_number',
                component: truncatedTextDisplay,
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/codebook_type.road_type'),
                key: 'road_type',
                always_visible: true
              },
              {
                label: this.$t('road-maintenance/aggregator.city_district'),
                key: 'city_district',
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/aggregator.class'),
                key: 'doc_class',
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/dropdown-types.applicant'),
                key: 'applicant',
                component: truncatedTextDisplay,
                initial_value: true
              },
              {
                label: this.$t('fleet/vehicle.status'),
                key: 'status',
                style: {
                  width: '120px'
                },
                always_visible: true
              },
              {
                label: this.$t('road-maintenance/aggregator.request'),
                key: 'document_code',
                initial_value: true
              }
            ],
            paginationConfig: {
              perPageOptions: [20, 50, 100],
              perPage: 100,
              currentPage: 1
            },
            actionsConfig: [
              showDetailsAction(this.$t, (event) => this.showRequestDetails(event))
            ],
            filtersConfig: {
              fields: [
                {
                  key: 'road_id',
                  label: this.$t('road-maintenance/aggregator.street'),
                  type: 'combobox',
                  multiple: true,
                  clearable: true,
                  autocomplete_options: {
                    module: 'road-maintenance',
                    route: 'traffic-regulation/roads/autocomplete',
                    form_value: 'id',
                    allowSearch: true,
                    // filtering happens by an existing road's id, so typed
                    // text that matches no road can't be a valid filter value
                    allowCustomValue: false
                  }
                },
                {
                  key: 'road_type_id',
                  label: this.$t('road-maintenance/codebook_type.road_type'),
                  type: 'autocomplete',
                  clearable: true,
                  items: this.indexTableFiltersDropdowns?.road_type || [],
                  autocomplete_options: {
                    custom_display_value: (event) => {
                      return event?.description ? this.$t(event.description) : null
                    },
                    form_value: 'id'
                  }
                },
                {
                  key: 'city_district_id',
                  label: this.$t('road-maintenance/aggregator.city_district'),
                  type: 'autocomplete',
                  clearable: true,
                  autocomplete_options: {
                    module: 'road-maintenance',
                    route: 'traffic-regulation/city-districts',
                    allowSearch: false,
                    form_value: 'id'
                  }
                },
                {
                  key: 'status_id',
                  label: this.$t('fleet/vehicle.status'),
                  type: 'autocomplete',
                  clearable: true,
                  autocomplete_options: {
                    module: 'road-maintenance',
                    route: 'traffic-regulation/statuses',
                    allowSearch: false,
                    custom_display_value: (value) => {
                      const { description } = value || null
                      return description && typeof description === 'string' ? this.$t(description) : ''
                    },
                    form_value: 'id'
                  }
                },
                {
                  key: 'address_number',
                  label: this.$t('road-maintenance/aggregator.house_number')
                },
                {
                  key: 'applicant',
                  label: this.$t('road-maintenance/prp.request_applicant')
                }
              ]
            }
          }
        },
        breadcrumbs: {
          items: [
            {
              text: this.$t('road-maintenance.eRegulation')
            },
            {
              text: this.$t('road-maintenance/aggregator.streets_overview'),
              bolded: true
            }
          ]
        }
      }
    }
  },

  created () {
    this.fetchCodebooks().then()
  },

  methods: {
    async fetchCodebooks () {
      try {
        const { data } = await api('road-maintenance').get('traffic-regulation/codebook', {
          types: [
            'ROAD_TYPE'
          ]
        })
        this.indexTableFiltersDropdowns = data || {}
      }
      catch (exception) {
        console.error(exception)
      }
    },

    onExportRoads (event) {
      const { filters } = event?.tableFiltersPreviewConfig || {}
      const query = {
        filters: filters || {}
      }
      const apiConfig = {
        module: 'road-maintenance',
        route: 'traffic-regulation/roads/export'
      }
      const exportName = `${this.$t('road-maintenance/aggregator.export_filename')}_${dateService.now(FORMATS.filename)}.xlsx`
      this.export({ apiConfig, query }, exportName)
    },

    showRequestDetails (event) {
      const { data } = event || {}
      const { regulation_id: regulationId, request_type: requestType } = data || {}

      // skip if data is not valid
      if (!regulationId || !requestType) {
        notify(this.$t('road-maintenance/aggregator.request_not_found'))
        return
      }

      const query = {
        category: 'request_overview',
        request_type: requestType || '',
        from: 'Eregulation-roads'
      }

      // show request details
      this.$router.push({
        name: 'Aggregator_requests_details',
        params: {
          id: regulationId
        },
        query
      }).then()
    }
  }
}
